<script setup>
/**
 * Answers — the knowledge library. THE PLAN, §4 P5.
 *
 * The whole screen is three questions in order: what is happening right now, what did not
 * work, and what does my assistant actually know. Everything else was cut.
 *
 * ── THE THREE THINGS THIS PIECE IS THE FIX FOR ────────────────────────────────────────
 *
 * 1. THREE FORMS BECAME ONE CHOICE. The screen this replaces stacked a paste-text form, a
 *    crawl form and an upload form permanently above the library, so an owner who wanted
 *    the third scrolled past two she did not want, every visit, and the page opened
 *    looking like paperwork. Here there is one primary button; the choice lives in a
 *    dialog and only one form exists at a time. Five clicks from cold, §3.
 *
 * 2. THE EMPTY STATE DOES NOT PAINT FAKE ROWS. `EmptyStateLayout` defaults to
 *    `showBackdrop: true`, which draws placeholder rows at 50% opacity under a gradient
 *    scrim. Four independent critics read Captain's documents page that way and all four
 *    called it BROKEN — half-loaded, greyed-out, disabled. It is the single most
 *    misread pattern in the reference set, so this page passes `:show-backdrop="false"`
 *    and says what to do in words. A first-run screen has exactly one job and cannot
 *    afford to look like a failure.
 *
 * 3. NOTHING IS DERIVED FROM AN ABSENCE — the rule the section is built on. Two
 *    independent reads: the library and the background work. The library IS the page, so
 *    its failure is the page's failure; the job read degrades in place behind a banner
 *    that names what could not be checked, because a silently empty "just added" section
 *    on an account with a crawl running is indistinguishable from a calm one.
 *
 * ── WHY THE POLL LADDER IS A COMPOSABLE AND NOT A `setInterval` ───────────────────────
 * `useJobPoller` carries the 2s → 5s → 15s → stop schedule the htmx panel learned the
 * expensive way (four concurrent sources once ate the sidecar's whole per-account budget
 * and 429'd the owner's next click). One poll for the WHOLE page, not one per card, and a
 * hard stop that this screen renders as a sentence with a "check again" button rather than
 * as a spinner that quietly stopped meaning anything.
 *
 * A job that finishes while the page is open triggers ONE library reload, and its card
 * stays on screen saying "ready to use" with a link into what it produced. A card that
 * simply vanished would be the only feedback an owner ever got that her upload worked.
 */
import { computed, ref, watch } from 'vue';

import { useAlert } from 'dashboard/composables';
import Banner from 'dashboard/components-next/banner/Banner.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import EmptyStateLayout from 'dashboard/components-next/EmptyStateLayout.vue';
import SettingsHeader from 'dashboard/components-next/captain/pageComponents/settings/SettingsHeader.vue';

import AddKnowledgeDialog from '../components/AddKnowledgeDialog.vue';
import AnswerList from '../components/AnswerList.vue';
import NoticeBox from '../components/NoticeBox.vue';
import JobProgressCard from '../components/JobProgressCard.vue';
import SourceCard from '../components/SourceCard.vue';
import TeamSuggestions from '../components/TeamSuggestions.vue';
import ClaudeAnswersList from '../components/ClaudeAnswersList.vue';
import WasloExplainer from '../components/WasloExplainer.vue';
import WasloPageLayout from '../components/WasloPageLayout.vue';
import RecentlyRemoved from '../components/RecentlyRemoved.vue';
import TestItButton from '../components/TestItButton.vue';
import { useWasloI18n } from '../i18n.js';
import { useWasloApi } from '../composables/useWasloApi.js';
import { useWasloAccountId } from '../composables/useWasloAccountId.js';
import { useJobPoller } from '../composables/useJobPoller.js';
import { wasloApi } from '../api/client.js';

const { t } = useWasloI18n();
const accountId = useWasloAccountId();

// `isEmpty: () => false` on both. The composable's default treats `[]` as the EMPTY
// state, which is right for a page whose whole content is one list — but this page has
// two, and each empty list MEANS something different ("nothing is being read", "nothing
// has been added yet"). The page decides its own emptiness below, from both at once.
const documents = useWasloApi(
  signal => wasloApi.getDocuments(accountId.value, signal),
  { isEmpty: () => false }
);
const jobs = useWasloApi(signal => wasloApi.getJobs(accountId.value, signal), {
  isEmpty: () => false,
});

// Only the library can fail the page. See the block comment above.
const errorMessage = computed(() => documents.errorMessage.value);

const isFetching = computed(
  () =>
    !errorMessage.value && (documents.isFetching.value || jobs.isFetching.value)
);

const sources = computed(() => documents.data.value ?? []);
const jobList = computed(() => jobs.data.value ?? []);
/** Named, not inferred: an unread job list must never render as "nothing is running". */
const jobsUnavailable = computed(() => Boolean(jobs.error.value));

const isActive = job => job.status === 'queued' || job.status === 'running';

// ── which jobs this screen is responsible for ──────────────────────────────────────
// Every job that is still moving, every job that failed, and — this is the part that is
// easy to miss — every job that FINISHED WHILE THE OWNER WAS WATCHING. Without the last
// one, pressing Add produces a card that appears, spins, and disappears, which is
// indistinguishable from a card that crashed.
const watching = ref(new Set());
const settled = ref(new Set());
// The jobs THIS visit queued (P-5.12): only their cards print a receipt, because only there
// did the owner just confirm a price.
const queuedHere = ref(new Set());

const activeJobs = computed(() =>
  jobList.value.filter(
    job =>
      isActive(job) ||
      (job.status === 'succeeded' && watching.value.has(job.id))
  )
);

// Dismissed here are hidden at once, before the job list is read again: the row the
// owner just removed must not come back for one poll.
const dismissed = ref(new Set());
const dismissing = ref(new Set());

const failedJobs = computed(() =>
  jobList.value.filter(
    job => job.status === 'failed' && !dismissed.value.has(job.id)
  )
);

const poller = useJobPoller(() => jobs.reload());

watch(jobList, list => {
  let finished = false;
  list.forEach(job => {
    if (isActive(job)) {
      watching.value.add(job.id);
      return;
    }
    // A job we were watching has landed. Reload the library ONCE — the source it produced
    // only exists from this moment, and a poll that kept refetching the whole library
    // every two seconds would be the expensive version of this same feature.
    if (
      job.status === 'succeeded' &&
      watching.value.has(job.id) &&
      !settled.value.has(job.id)
    ) {
      settled.value.add(job.id);
      finished = true;
    }
  });
  if (finished) documents.reload();
  if (list.some(isActive)) poller.start();
  else poller.stop();
});

/** The hard stop only means anything while something is still supposed to be moving. */
const isStalled = computed(
  () => poller.isStalled.value && jobList.value.some(isActive)
);

// ── the add flow ───────────────────────────────────────────────────────────────────
const dialogRef = ref(null);

const openDialog = () => dialogRef.value?.open();

/** `url_crawl` upstream, `url` on the wire — map both, and never guess past them. */
const choiceFor = sourceType => {
  if (typeof sourceType === 'string' && sourceType.startsWith('url')) {
    return 'url';
  }
  if (sourceType === 'image') return 'photos';
  return sourceType === 'pdf' ? 'pdf' : 'text';
};

/**
 * "Add it again" on a source that could not be read. The NAME is carried over and the
 * content deliberately is not: the thing that failed is exactly the thing that has to
 * change, and pre-filling the address that timed out would invite pressing Add on it
 * twice.
 */
const addAgain = job =>
  dialogRef.value?.open({ type: choiceFor(job.sourceType), title: job.title });

/**
 * "Dismiss" on a source that could not be read. Nothing is lost by it — nothing from a
 * failed source was ever saved — so there is no confirmation step, and the row goes the
 * moment the BFF says so. On failure the row stays, and says why in a toast.
 */
const dismiss = async job => {
  if (dismissing.value.has(job.id)) return;
  dismissing.value.add(job.id);
  try {
    await wasloApi.dismissJob(accountId.value, job.id);
    dismissed.value.add(job.id);
    jobs.reload();
  } catch {
    useAlert(t('WASLO.ANSWERS.WORKING.DISMISS_FAILED'));
  } finally {
    dismissing.value.delete(job.id);
  }
};

const onQueued = job => {
  useAlert(
    job.deduped
      ? t('WASLO.ANSWERS.DIALOG.DEDUPED')
      : t('WASLO.ANSWERS.DIALOG.QUEUED')
  );
  // Watch it from this moment, so its card survives finishing.
  watching.value.add(job.id);
  queuedHere.value.add(job.id);
  // `resume` rather than `start`: somebody is watching again, so the ladder goes back to
  // its fast rungs and polls immediately instead of waiting out a 15-second gap.
  poller.resume();
};

const reload = () => {
  documents.reload();
  poller.resume();
};

/** C3: a change that can move an answer (a suggestion taken, a thing put back) redraws the list. */
const answersKey = ref(0);
const reloadAnswers = () => {
  reload();
  answersKey.value += 1;
};

/**
 * "There is nothing here" is a CLAIM, and it is only made when both reads answered. With
 * the job list unread it would be a guess — and the one it would get wrong is the owner
 * who has just added her first source and is watching for it.
 */
const isEmpty = computed(
  () =>
    !isFetching.value &&
    !errorMessage.value &&
    !jobsUnavailable.value &&
    sources.value.length === 0 &&
    activeJobs.value.length === 0 &&
    failedJobs.value.length === 0
);
</script>

<template>
  <WasloPageLayout
    :header-title="t('WASLO.ANSWERS.TITLE')"
    :is-fetching="isFetching"
    :not-provisioned="documents.isNotProvisioned.value"
    :error-message="errorMessage"
    :is-empty="isEmpty"
    :button-label="isEmpty ? '' : t('WASLO.ANSWERS.ADD')"
    @click="openDialog"
    @retry="reload"
  >
    <template #action>
      <TestItButton />
    </template>

    <template #body>
      <div class="flex flex-col gap-8 pb-10">
        <WasloExplainer :note="t('WASLO.ANSWERS.SPOTLIGHT')" />

        <!-- 1 · WHAT IS HAPPENING RIGHT NOW. -->
        <section
          v-if="activeJobs.length || jobsUnavailable"
          class="flex flex-col gap-4"
          data-waslo-working
        >
          <SettingsHeader
            :heading="t('WASLO.ANSWERS.WORKING.HEADING')"
            :description="t('WASLO.ANSWERS.WORKING.DESCRIPTION')"
          />
          <!-- A read that failed is NAMED, never rendered as "nothing is being read". -->
          <Banner
            v-if="jobsUnavailable"
            color="slate"
            data-waslo-working-unavailable
          >
            {{ t('WASLO.ANSWERS.WORKING.UNAVAILABLE') }}
          </Banner>
          <ul
            v-else
            class="flex flex-col w-full divide-y outline outline-1 -outline-offset-1 divide-n-weak outline-n-container rounded-xl bg-n-solid-2"
            data-waslo-working-list
          >
            <JobProgressCard
              v-for="job in activeJobs"
              :key="job.id"
              :job="job"
              :account-id="accountId"
              :is-stalled="isStalled"
              :show-receipt="queuedHere.has(job.id)"
              @check="poller.resume()"
              @again="addAgain"
            />
          </ul>
        </section>

        <!-- 2 · WHAT DID NOT WORK. Its own section, because "could not be read" under a
             heading that says "just added" is the kind of small lie this section is
             built to stop telling. -->
        <section
          v-if="failedJobs.length"
          class="flex flex-col gap-4"
          data-waslo-failed
        >
          <SettingsHeader
            :heading="t('WASLO.ANSWERS.WORKING.FAILED_HEADING')"
            :description="t('WASLO.ANSWERS.WORKING.FAILED_DESCRIPTION')"
          />
          <ul
            class="flex flex-col w-full divide-y outline outline-1 -outline-offset-1 divide-n-weak outline-n-container rounded-xl bg-n-solid-2"
            data-waslo-failed-list
          >
            <JobProgressCard
              v-for="job in failedJobs"
              :key="job.id"
              :job="job"
              :account-id="accountId"
              :is-dismissing="dismissing.has(job.id)"
              :show-receipt="queuedHere.has(job.id)"
              @again="addAgain"
              @dismiss="dismiss"
            />
          </ul>
        </section>

        <!-- E2 (P-6.2, P-8.4): the answers Claude drafted, kept or removed here. Hidden when none waits. -->
        <ClaudeAnswersList :account-id="accountId" @changed="reloadAnswers" />
        <!-- P-1.7: what the team suggested from the chat pane. Hidden when none waits. -->
        <TeamSuggestions :account-id="accountId" @changed="reloadAnswers" />
        <!-- 3 · WHAT IT KNOWS. -->
        <section class="flex flex-col gap-4" data-waslo-library>
          <SettingsHeader
            :heading="t('WASLO.ANSWERS.LIBRARY.HEADING')"
            :description="t('WASLO.ANSWERS.LIBRARY.DESCRIPTION')"
          />
          <ul
            v-if="sources.length"
            class="flex flex-col w-full divide-y outline outline-1 -outline-offset-1 divide-n-weak outline-n-container rounded-xl bg-n-solid-2"
            data-waslo-library-list
          >
            <SourceCard
              v-for="source in sources"
              :key="source.id"
              :source="source"
              :account-id="accountId"
            />
          </ul>
          <p v-else class="mb-0 text-sm text-n-slate-11">
            {{ t('WASLO.ANSWERS.EMPTY.BODY') }}
          </p>
        </section>

        <!-- 4 · EVERY ANSWER, and the controls to fix one (C3, P-6.1). -->
        <section class="flex flex-col gap-4" data-waslo-all-answers>
          <SettingsHeader
            :heading="t('WASLO.ALL_ANSWERS.HEADING')"
            :description="t('WASLO.ALL_ANSWERS.DESCRIPTION')"
          />
          <AnswerList :key="answersKey" :account-id="accountId" />
        </section>

        <!-- 5 · TRUE FOR NOW (C3, P-6.9). -->
        <NoticeBox :account-id="accountId" />

        <!-- 6 · WHAT WAS REMOVED (P-6.11). Hidden until something was. -->
        <RecentlyRemoved
          :account-id="accountId"
          :kinds="['source', 'answer', 'notice']"
          @restored="reloadAnswers"
        />
      </div>
    </template>

    <template #emptyState>
      <!--
        `:show-backdrop="false"` IS THE POINT OF THIS SLOT. The default paints placeholder
        rows behind a gradient — see the header. Four critics, four independent reads of
        the same pattern, one verdict: broken.
      -->
      <EmptyStateLayout
        :title="t('WASLO.ANSWERS.EMPTY.TITLE')"
        :subtitle="t('WASLO.ANSWERS.EMPTY.BODY')"
        :show-backdrop="false"
        :action-perms="['administrator']"
        data-waslo-answers-empty
      >
        <template #actions>
          <Button
            :label="t('WASLO.ANSWERS.ADD')"
            icon="i-lucide-plus"
            data-waslo-answers-empty-add
            @click="openDialog"
          />
        </template>
      </EmptyStateLayout>
      <!-- A workspace with no source yet can still have Claude's answers waiting (E2, P-8.4). -->
      <ClaudeAnswersList :account-id="accountId" @changed="reload" />
      <!-- A workspace with no source yet can still have its team's suggestions waiting. -->
      <TeamSuggestions :account-id="accountId" @changed="reload" />
      <!-- Removing the last source empties the page, which is exactly when Put it back matters. -->
      <RecentlyRemoved
        :account-id="accountId"
        :kinds="['source', 'answer', 'notice']"
        @restored="reloadAnswers"
      />
    </template>
  </WasloPageLayout>

  <!-- Outside the frame: it teleports to <body> and must not be unmounted by the state
       branches inside the layout. -->
  <AddKnowledgeDialog
    ref="dialogRef"
    :account-id="accountId"
    @queued="onQueued"
  />
</template>
